import { capturePreviewSnapshot } from '@/utils/capturePreview';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Shirt } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCart } from '@/context/CartContext';
import FabricStep from '@/components/FabricStep';
import OptionsStep, { PIPING_OPTIONS } from '@/components/OptionsStep';
import HingedCushion3DPreview from '@/components/HingedCushion3DPreview';
import FoamInlineSection from '@/components/FoamInlineSection';
import ThicknessButtons from '@/components/ThicknessButtons';
import { FOAM_BY_THICKNESS, FOAM_DENSITIES } from '@/components/cushionConstants';
import { CustomizerLayout, ProductTypeStep, buildProductImages, PreviewGallery, ReviewStep, DimensionField } from '@/components/customizer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CUSHION_ID = 'hinged-chair-cushion';

const GALLERY_IMAGES = [
  { src: 'https://images.unsplash.com/photo-1759722667224-a5a7143fcbcf?w=800&q=80', alt: 'Two piece cushion on bench' },
  { src: 'https://images.unsplash.com/photo-1768573264507-1d5cbe1f0023?w=800&q=80', alt: 'Sofa back cushion detail' },
  { src: 'https://images.unsplash.com/photo-1768332572728-2fff6ecbd6b6?w=800&q=80', alt: 'Seating with back cushion' },
  { src: 'https://images.unsplash.com/photo-1759722665614-265fdf133b37?w=800&q=80', alt: 'Sectional with hinge cushions' },
];

const PRODUCT_TYPE_OPTIONS = [
  { value: 'cover_and_foam', label: 'Cover & Foam Insert', description: 'Complete cushion with cover and foam', icon: Package },
  { value: 'cover_only', label: 'Cover Only', description: 'Just the fabric cover, no foam', icon: Shirt },
];

const HingedCushionPage = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [productType, setProductType] = useState('cover_and_foam');
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const stepContentRef = useRef(null);

  const [cushion, setCushion] = useState(null);
  // length=A(width), width=B(seat depth), back_height=C, thickness=D
  const [dimensionsCm, setDimensionsCm] = useState({ length: 0, width: 0, thickness: null, back_height: 0 });
  const [inputValues, setInputValues] = useState({ length: '', width: '', back_height: '' });
  const [unit, setUnit] = useState('cm');

  const [selectedFabric, setSelectedFabric] = useState(null);
  const [selectedFoam, setSelectedFoam] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [priceInfo, setPriceInfo] = useState(null);
  const [selectedFabricRange, setSelectedFabricRange] = useState(null);
  const [selectedFabricColor, setSelectedFabricColor] = useState(null);
  const [fabricZoom, setFabricZoom] = useState(3);
  const [fabricRanges, setFabricRanges] = useState([]);
  const [pipingOption, setPipingOption] = useState('none');
  const [pipingFabricRange, setPipingFabricRange] = useState(null);
  const [pipingFabricColor, setPipingFabricColor] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [searchFabricData, setSearchFabricData] = useState(null);

  const steps = useMemo(() => {
    const base = [{ name: 'Product', description: 'Select type' }, { name: 'Dimensions', description: 'Enter measurements' }];
    if (productType === 'cover_and_foam') return [...base, { name: 'Fabric', description: 'Choose fabric' }, { name: 'Foam', description: 'Select foam' }, { name: 'Options', description: 'Add extras' }, { name: 'Review', description: 'Confirm order' }];
    return [...base, { name: 'Fabric', description: 'Choose fabric' }, { name: 'Options', description: 'Add extras' }, { name: 'Review', description: 'Confirm order' }];
  }, [productType]);
  const currentStepName = steps[currentStep]?.name || 'Product';

  const cmToUnit = useCallback((cm, u) => u === 'mm' ? cm * 10 : cm, []);
  const unitToCm = useCallback((v, u) => u === 'mm' ? v / 10 : v, []);

  const getDisplayValue = useCallback((field) => {
    if (inputValues[field] !== undefined && inputValues[field] !== '') return inputValues[field];
    const v = cmToUnit(dimensionsCm[field], unit);
    return v === 0 ? '' : v;
  }, [dimensionsCm, unit, cmToUnit, inputValues]);

  const handleDimensionInput = (field, rawValue) => {
    const filtered = rawValue.replace(/[^0-9.]/g, '');
    const parts = filtered.split('.');
    const clean = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : filtered;
    setInputValues(prev => ({ ...prev, [field]: clean }));
    if (clean === '') setDimensionsCm(prev => ({ ...prev, [field]: 0 }));
    else { const n = parseFloat(clean); if (!isNaN(n)) setDimensionsCm(prev => ({ ...prev, [field]: unitToCm(n, unit) })); }
  };
  const handleDimensionBlur = (field) => { setInputValues(prev => ({ ...prev, [field]: '' })); setActiveField(null); };
  const handleDimensionFocus = (field) => {
    const v = cmToUnit(dimensionsCm[field], unit);
    setInputValues(prev => ({ ...prev, [field]: v === 0 ? '' : String(v) }));
    // Map field name → preview active field
    const mapping = { length: 'width', width: 'seatDepth', back_height: 'backHeight' };
    setActiveField(mapping[field] || field);
  };

  const getLimitInUnit = useCallback((field, type) => {
    const limits = {
      length: { min: cushion?.min_length || 20, max: cushion?.max_length || 100 },
      width: { min: cushion?.min_width || 20, max: cushion?.max_width || 100 },
      back_height: { min: 20, max: 250 },
    };
    return cmToUnit(limits[field]?.[type] || 0, unit);
  }, [cushion, unit, cmToUnit]);

  const handleThicknessChange = (v) => {
    const t = parseFloat(v);
    setDimensionsCm(prev => ({ ...prev, thickness: t }));
    const avail = FOAM_BY_THICKNESS[t] || [];
    if (selectedFoam && !avail.includes(selectedFoam)) setSelectedFoam(null);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [c, fr] = await Promise.all([axios.get(`${API}/cushion-types/${CUSHION_ID}`), axios.get(`${API}/fabric-ranges`)]);
        setCushion(c.data); setFabricRanges(fr.data);
      } catch { toast.error('Failed to load product data'); }
      finally { setLoading(false); }
    })();
  }, []);

  const selectedFabricRangeObj = useMemo(() => fabricRanges.find(r => r.id === selectedFabricRange), [fabricRanges, selectedFabricRange]);
  const selectedFabricColorObj = useMemo(() => selectedFabricRangeObj?.colors?.find(c => c.id === selectedFabricColor), [selectedFabricRangeObj, selectedFabricColor]);
  const selectedFoamObj = selectedFoam ? FOAM_DENSITIES[selectedFoam] : null;
  const fabricColorForPreview = selectedFabricColorObj?.hex_color || null;
  const fabricImageUrl = useMemo(() => {
    if (searchFabricData?.imageUrl) return searchFabricData.imageUrl;
    const u = selectedFabricColorObj?.image_url;
    return (u?.includes('warwick.com.au') || u?.startsWith('/api/fabric-images')) ? u : null;
  }, [selectedFabricColorObj, searchFabricData]);
  const displayFabricName = useMemo(() => {
    if (searchFabricData) return `${searchFabricData.rangeName} - ${searchFabricData.colorName}`;
    return (selectedFabricRangeObj && selectedFabricColorObj) ? `${selectedFabricRangeObj.name} - ${selectedFabricColorObj.name}` : '-';
  }, [selectedFabricRangeObj, selectedFabricColorObj, searchFabricData]);
  const pipingCost = useMemo(() => PIPING_OPTIONS.find(o => o.value === pipingOption)?.price || 0, [pipingOption]);
  const pipingHexColor = useMemo(() => {
    if (pipingOption === 'different' && pipingFabricRange && pipingFabricColor) {
      const pRange = fabricRanges.find(r => r.id === pipingFabricRange);
      return pRange?.colors?.find(c => c.id === pipingFabricColor)?.hex_color || '#f5f0eb';
    }
    return null;
  }, [pipingOption, pipingFabricRange, pipingFabricColor, fabricRanges]);
  const pipingImageUrlComputed = useMemo(() => {
    if (pipingOption === 'same') return fabricImageUrl;
    if (pipingOption === 'different' && pipingFabricRange && pipingFabricColor) {
      const pRange = fabricRanges.find(r => r.id === pipingFabricRange);
      const pColor = pRange?.colors?.find(c => c.id === pipingFabricColor);
      const u = pColor?.image_url;
      return (u?.includes('warwick.com.au') || u?.startsWith('/api/fabric-images')) ? u : null;
    }
    return null;
  }, [pipingOption, fabricImageUrl, pipingFabricRange, pipingFabricColor, fabricRanges]);

  const calculatePrice = useCallback(async () => {
    if (!cushion) return;
    const hasFabric = (selectedFabricRange && selectedFabricColor) || selectedFabric;
    if (productType === 'cover_and_foam' && (!hasFabric || !selectedFoam)) return;
    if (productType === 'cover_only' && !hasFabric) return;
    try {
      const r = await axios.post(`${API}/calculate-price`, {
        cushion_type_id: CUSHION_ID,
        dimensions: dimensionsCm,
        product_type: productType,
        fabric_id: selectedFabricRange ? null : selectedFabric,
        fabric_range_id: selectedFabricRange,
        fabric_color_id: selectedFabricColor,
        foam_type_id: productType !== 'cover_only' ? selectedFoam : null,
        quantity
      });
      setPriceInfo(r.data);
    } catch (e) { console.error('Price error:', e); }
  }, [cushion, dimensionsCm, productType, selectedFabric, selectedFabricRange, selectedFabricColor, selectedFoam, quantity]);

  useEffect(() => { const t = setTimeout(calculatePrice, 300); return () => clearTimeout(t); }, [calculatePrice]);

  const canProceed = () => {
    if (currentStepName === 'Dimensions') {
      return dimensionsCm.length > 0 && dimensionsCm.width > 0 && dimensionsCm.back_height > 0 && dimensionsCm.thickness >= 5;
    }
    if (currentStepName === 'Foam') return !!selectedFoam;
    if (currentStepName === 'Fabric') return (selectedFabricRange && selectedFabricColor) || selectedFabric;
    return true;
  };
  const nextStep = () => { if (currentStep < steps.length - 1) { setCurrentStep(currentStep + 1); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50); } };
  const prevStep = () => { if (currentStep > 0) { setCurrentStep(currentStep - 1); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50); } };

  const handleAddToCart = async () => {
    if (!priceInfo) return;
    setAddingToCart(true);
    const preview_image = await capturePreviewSnapshot();
    try {
      const ok = await addToCart({
        cushion_type_id: CUSHION_ID,
        dimensions: dimensionsCm,
        product_type: productType,
        fabric_id: selectedFabricRange ? null : selectedFabric,
        fabric_range_id: selectedFabricRange,
        fabric_color_id: selectedFabricColor,
        foam_type_id: productType !== 'cover_only' ? selectedFoam : null,
        quantity,
        piping_option: pipingOption !== 'none' ? pipingOption : null,
        piping_fabric_range_id: pipingOption === 'different' ? pipingFabricRange : null,
        piping_fabric_color_id: pipingOption === 'different' ? pipingFabricColor : null,
        preview_image
      });
      if (ok) toast.success('Added to cart!', { action: { label: 'View Cart', onClick: () => navigate('/cart') } });
    } catch { toast.error('Failed'); }
    setAddingToCart(false);
  };

  const configRows = useMemo(() => {
    const u = unit;
    const fmt = (v) => v > 0 ? `${u === 'mm' ? v * 10 : v} ${u}` : '-';
    const rows = [
      { label: 'Product Type', value: PRODUCT_TYPE_OPTIONS.find(p => p.value === productType)?.label || '-' },
      { label: 'Width (A)', value: fmt(dimensionsCm.length) },
      { label: 'Seat Depth (B)', value: fmt(dimensionsCm.width) },
      { label: 'Back Height (C)', value: fmt(dimensionsCm.back_height) },
      { label: 'Thickness (D)', value: dimensionsCm.thickness ? `${u === 'mm' ? dimensionsCm.thickness * 10 : dimensionsCm.thickness} ${u}` : '-' },
    ];
    if (fabricColorForPreview || searchFabricData) rows.push({ label: 'Fabric', value: <span className="flex items-center justify-end gap-2"><span className="w-4 h-4 rounded-full border" style={{ backgroundColor: fabricColorForPreview }}></span>{displayFabricName}</span> });
    if (productType !== 'cover_only' && selectedFoamObj) rows.push({ label: 'Foam', value: selectedFoamObj.name });
    if (pipingOption !== 'none') {
      const pLabel = PIPING_OPTIONS.find(o => o.value === pipingOption)?.label || '-';
      if (pipingOption === 'different') {
        const pRange = fabricRanges.find(r => r.id === pipingFabricRange);
        const pColor = pRange?.colors?.find(c => c.id === pipingFabricColor);
        rows.push({ label: 'Piping', value: pColor ? <span className="flex items-center justify-end gap-2"><span className="w-4 h-4 rounded-full border" style={{ backgroundColor: pColor.hex_color }}></span>{pRange.name} - {pColor.name}</span> : pLabel });
      } else {
        rows.push({ label: 'Piping', value: pLabel });
      }
    }
    return rows;
  }, [productType, dimensionsCm, unit, fabricColorForPreview, displayFabricName, searchFabricData, selectedFoamObj, pipingOption, fabricRanges, pipingFabricRange, pipingFabricColor]);

  const previewPanel = (
    <PreviewGallery galleryImages={GALLERY_IMAGES} galleryIndex={galleryIndex} setGalleryIndex={setGalleryIndex} configRows={configRows} priceInfo={priceInfo} pipingCost={pipingCost} showZipLabel={productType !== 'foam_only'}>
      <HingedCushion3DPreview
        width={dimensionsCm.length > 0 ? dimensionsCm.length : 50}
        seatDepth={dimensionsCm.width > 0 ? dimensionsCm.width : 50}
        backHeight={dimensionsCm.back_height > 0 ? dimensionsCm.back_height : 65}
        thickness={dimensionsCm.thickness || 7.5}
        rawWidth={dimensionsCm.length}
        rawSeatDepth={dimensionsCm.width}
        rawBackHeight={dimensionsCm.back_height}
        rawThickness={dimensionsCm.thickness}
        unit={unit}
        fabricColor={fabricColorForPreview}
        fabricImageUrl={fabricImageUrl}
        fabricZoom={fabricZoom}
        showZip={productType !== 'foam_only'}
        activeField={activeField}
        pipingOption={pipingOption}
        pipingColor={pipingHexColor}
        pipingImageUrl={pipingImageUrlComputed}
      />
    </PreviewGallery>
  );

  const contentPanel = (
    <div ref={stepContentRef}>
      {currentStepName === 'Product' && <ProductTypeStep options={PRODUCT_TYPE_OPTIONS} value={productType} onChange={setProductType} productName="Hinged Cushion" productDescription="Seat and back cushion set connected by a fabric hinge — perfect for bench seats, window seats, and built-in banquettes where you need an integrated back and base that move together." productImages={buildProductImages(GALLERY_IMAGES)} />}
      {currentStepName === 'Dimensions' && (
        <div data-testid="step-dimensions">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <h2 className="text-lg font-semibold text-[#1A1A1A]">Enter Dimensions</h2>
            <Tabs value={unit} onValueChange={setUnit} className="w-auto">
              <TabsList className="h-7 sm:h-9 bg-sand p-0.5">
                <TabsTrigger value="cm" className="text-xs sm:text-sm px-2 sm:px-3 h-6 sm:h-8 data-[state=active]:bg-bush data-[state=active]:text-white rounded" data-testid="unit-cm">cm</TabsTrigger>
                <TabsTrigger value="mm" className="text-xs sm:text-sm px-2 sm:px-3 h-6 sm:h-8 data-[state=active]:bg-bush data-[state=active]:text-white rounded" data-testid="unit-mm">mm</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <p className="hidden sm:block text-gray-600 mb-6 text-sm">Hinged cushion has four dimensions: Width (A), Seat Depth (B), Back Height (C) and Thickness (D). A 2cm hinge flap joins the seat and back (inset 1cm on each end).</p>
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-3 sm:gap-6">
            <div className="col-span-2 sm:col-span-1">
              <DimensionField label="Width" shortLabel="A" field="length" value={getDisplayValue('length')} onChange={handleDimensionInput} onBlur={handleDimensionBlur} onFocus={handleDimensionFocus} unit={unit} helpText="The shared width of both the seat and back cushions" limitText={`${getLimitInUnit('length','min')}-${getLimitInUnit('length','max')}${unit}`} activeTooltip={activeTooltip} setActiveTooltip={setActiveTooltip} testId="width-input" />
            </div>
            <DimensionField label="Seat Depth" shortLabel="B" field="width" value={getDisplayValue('width')} onChange={handleDimensionInput} onBlur={handleDimensionBlur} onFocus={handleDimensionFocus} unit={unit} helpText="The front-to-back depth of the seat cushion" limitText={`${getLimitInUnit('width','min')}-${getLimitInUnit('width','max')}${unit}`} activeTooltip={activeTooltip} setActiveTooltip={setActiveTooltip} testId="seat-depth-input" />
            <DimensionField label="Back Height" shortLabel="C" field="back_height" value={getDisplayValue('back_height')} onChange={handleDimensionInput} onBlur={handleDimensionBlur} onFocus={handleDimensionFocus} unit={unit} helpText="The total height of the back cushion" limitText={`${getLimitInUnit('back_height','min')}-${getLimitInUnit('back_height','max')}${unit}`} activeTooltip={activeTooltip} setActiveTooltip={setActiveTooltip} testId="back-height-input" />
            <div className="col-span-2 sm:col-span-1 space-y-1 sm:space-y-2">
              <Label className="text-xs sm:text-base font-semibold flex items-center gap-1 sm:gap-2">
                <span className="w-5 h-5 sm:w-8 sm:h-8 rounded-full bg-bush/10 flex items-center justify-center text-bush text-[10px] sm:text-sm font-bold">D</span>
                <span>Thickness</span>
              </Label>
              <ThicknessButtons value={dimensionsCm.thickness} onChange={handleThicknessChange} unit={unit} />
              <p className="hidden sm:block text-xs text-gray-500 mt-2">Applies to both seat and back cushions.</p>
            </div>
          </div>
        </div>
      )}
      {currentStepName === 'Foam' && <div data-testid="step-foam"><FoamInlineSection thicknessCm={dimensionsCm.thickness} selectedFoam={selectedFoam} onFoamSelect={setSelectedFoam} productType={productType} /></div>}
      {currentStepName === 'Fabric' && <FabricStep selectedRange={selectedFabricRange} selectedColor={selectedFabricColor} onRangeSelect={setSelectedFabricRange} onColorSelect={setSelectedFabricColor} cushionCategory={cushion.category} fabricZoom={fabricZoom} onFabricZoomChange={setFabricZoom} onSearchFabricSelect={setSearchFabricData} />}
      {currentStepName === 'Options' && <OptionsStep pipingOption={pipingOption} onPipingChange={setPipingOption} pipingFabricRange={pipingFabricRange} pipingFabricColor={pipingFabricColor} onPipingRangeSelect={setPipingFabricRange} onPipingColorSelect={setPipingFabricColor} cushionCategory={cushion.category} mainFabricRange={selectedFabricRange} mainFabricColor={selectedFabricColor} />}
      {currentStepName === 'Review' && (
        <ReviewStep
          cushionName={cushion.name}
          productType={productType}
          productTypeLabel={PRODUCT_TYPE_OPTIONS.find(p => p.value === productType)?.label}
          dimensionsSummary={`A ${dimensionsCm.length} × B ${dimensionsCm.width} × C ${dimensionsCm.back_height} × D ${dimensionsCm.thickness} cm`}
          fabricColorForPreview={fabricColorForPreview}
          displayFabricName={displayFabricName}
          selectedFoamName={selectedFoamObj?.name}
          pipingOption={pipingOption}
          pipingCost={pipingCost}
          pipingFabricName={pipingOption === 'different' ? (() => { const r = fabricRanges.find(r => r.id === pipingFabricRange); const c = r?.colors?.find(c => c.id === pipingFabricColor); return r && c ? `${r.name} - ${c.name}` : null; })() : null}
          quantity={quantity}
          setQuantity={setQuantity}
          priceInfo={priceInfo}
        />
      )}
    </div>
  );

  return (
    <CustomizerLayout
      cushion={cushion}
      loading={loading}
      steps={steps}
      currentStepIndex={currentStep}
      currentStepName={currentStepName}
      onStepClick={setCurrentStep}
      isFirstStep={currentStep === 0}
      isReviewStep={currentStepName === 'Review'}
      canProceed={canProceed()}
      onBack={prevStep}
      onNext={nextStep}
      onAddToCart={handleAddToCart}
      addingToCart={addingToCart}
      priceInfo={priceInfo}
      pipingCost={pipingCost}
      previewPanel={previewPanel}
      contentPanel={contentPanel}
      pageTestId="hinged-cushion-page"
    />
  );
};

export default HingedCushionPage;
